import * as THREE from 'three';
import { type OrbitControls } from 'three-stdlib';
import { LookAroundControls, type LookAroundControlsProps } from './LookAroundControls';

export enum NavigationControlType {
  Orbit,
  LookAround,
}

export interface NavigationControlsProps {
  lookAround?: LookAroundControlsProps;
  orbit?: {
    rotationSpeed?: number;
  };
}

export class NavigationControls {
  public camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;

  public domElement: HTMLElement;

  public orbitControls: OrbitControls;

  public lookAroundControls: LookAroundControls;

  public controlType: NavigationControlType = NavigationControlType.Orbit;

  public savedEnableDumping = false;

  public isEnabled = true;

  constructor(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    domElement: HTMLElement,
    orbitControls: OrbitControls,
    onMove?: () => void
  ) {
    this.camera = camera;
    this.domElement = domElement;

    this.orbitControls = orbitControls;
    this.savedEnableDumping = orbitControls.enableDamping;

    this.lookAroundControls = new LookAroundControls(this.camera, this.domElement, onMove);
    this.lookAroundControls.isEnabled = false;
  }

  setRotationSpeed(rotationSpeed: number) {
    this.orbitControls.rotateSpeed = rotationSpeed;
  }

  setIsEnabled(isEnabled: boolean) {
    if (this.isEnabled === isEnabled) {
      return;
    }
    this.isEnabled = isEnabled;

    if (this.controlType === NavigationControlType.Orbit) {
      this.orbitControls.enabled = isEnabled;
      if (!isEnabled) {
        this.savedEnableDumping = this.orbitControls.enableDamping;
        this.orbitControls.enablePan = false;
        this.orbitControls.enableRotate = false;
        this.orbitControls.enableZoom = false;
        this.orbitControls.enableDamping = false;
      } else {
        this.orbitControls.enablePan = true;
        this.orbitControls.enableRotate = true;
        this.orbitControls.enableZoom = true;
        this.orbitControls.enableDamping = this.savedEnableDumping;
      }
      //
    } else if (this.controlType === NavigationControlType.LookAround) {
      this.lookAroundControls.isEnabled = isEnabled;
    }
  }

  getTarget() {
    switch (this.controlType) {
      default:
      case NavigationControlType.Orbit:
        return this.orbitControls.target;
      case NavigationControlType.LookAround:
        return this.lookAroundControls.getTarget();
    }
  }

  private setTarget(target: THREE.Vector3) {
    switch (this.controlType) {
      case NavigationControlType.Orbit:
        this.orbitControls.target.copy(target);
        break;
      case NavigationControlType.LookAround:
        this.lookAroundControls.setTarget(target);
        break;
      default:
    }
  }

  setControlType(controlType: NavigationControlType) {
    if (this.controlType === controlType) {
      return;
    }
    const savedIsEnabled = this.isEnabled;
    const savedTarget = this.getTarget();

    this.setIsEnabled(false);
    this.controlType = controlType;
    if (savedIsEnabled) {
      this.lookAt(savedTarget);
    } else {
      this.setTarget(savedTarget);
    }
    this.setIsEnabled(savedIsEnabled);
  }

  lookAt(target: THREE.Vector3) {
    const { controlType, orbitControls, lookAroundControls } = this;
    switch (controlType) {
      case NavigationControlType.Orbit:
        orbitControls.target.copy(target);
        orbitControls.update();
        break;
      case NavigationControlType.LookAround:
        lookAroundControls.lookAt(target);
        break;
      default:
    }
  }

  calculateViewDirection(worldDirection: THREE.Vector3) {
    const { controlType } = this;
    switch (controlType) {
      default:
      case NavigationControlType.Orbit: {
        const { target } = this.orbitControls;
        const distance = this.camera.position.distanceTo(target);
        const position = worldDirection.clone();
        position.setLength(distance);
        position.add(target);
        return { position, lookAt: target };
      }
      case NavigationControlType.LookAround:
        return this.lookAroundControls.calculateViewDirection(worldDirection);
    }
  }

  dispose() {
    const { orbitControls, lookAroundControls } = this;
    orbitControls.dispose();
    lookAroundControls.dispose();
  }
}
